import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../main/Loader";
import {
  useFetchQrCodeQuery,
  useValidateOtpMutation
} from "../../store/services/AuthService";
import { decodeJwtAndSetCredentials } from "../../utils/decodeJwtAndSetCredentials";
import OTPInput, { OTPInputRef } from "../inputs/OTPInput";

interface TwoFactorRegistrationProps {
  userData: any;
  onSuccess: () => void;
}

export default function TwoFactorRegistration({ userData, onSuccess }: TwoFactorRegistrationProps) {
  const dispatch = useDispatch();
  const [otpCode, setOtpCode] = useState("");
  const otpInputRef = useRef<OTPInputRef>(null);

  const { data: qrBlob, isLoading: qrLoading, error: qrError } =
    useFetchQrCodeQuery({ twoFactorToken: userData.twoFactorToken });

  const [validateOtp, { isLoading }] = useValidateOtpMutation();
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  useEffect(() => {
    if (qrBlob) {
      const url = URL.createObjectURL(qrBlob);
      setQrUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [qrBlob]);

  // Focus automatico quando il componente si monta
  useEffect(() => {
    otpInputRef.current?.focus();
  }, []);

  const handleClear = () => {
    setOtpCode("");
    setTimeout(() => {
      otpInputRef.current?.focus();
    }, 0);
  }; 

  const handleSubmit = async () => {
    if (otpCode.length !== 6) {
      toast.warning("Inserisci un codice valido");
      return;
    }

    try {
      const res = await validateOtp({
        code: otpCode,
        token: userData.twoFactorToken,
      }).unwrap();

      decodeJwtAndSetCredentials(
        dispatch,
        onSuccess,
        {
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
        },
        "2FA configurato!",
        (msg) => toast.error(msg)
      );
    } catch {
      toast.error("Codice OTP errato");
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 0);
    }
  };

  if (qrError)
    return <div className="text-center my-8">Errore nel caricamento del QR code</div>;

  return (
    <div className="space-y-6 mt-10 text-center max-w-lg mx-auto">
      {qrLoading && <Loader />}

      {qrUrl && (
        <>
          <p>Scansiona il QR con Google Authenticator o Microsoft Authenticator.</p>
          <img src={qrUrl} className="mx-auto max-w-[200px]" alt="QR Code" />
        </>
      )}

      <p>Inserisci il codice a 6 cifre mostrato dall'app</p>

      <OTPInput ref={otpInputRef} value={otpCode} onChange={setOtpCode} />

      <div className="flex flex-col gap-y-4">
        <button
          onClick={handleSubmit}
          className="w-full bg-emerald-700 text-white font-semibold py-3 rounded-lg transition-colors"
        >
          Verifica
        </button>

        <button
          onClick={handleClear}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg transition-colors"
        >
          Cancella
        </button>
      </div>
    </div>
  );
}